<template>
  <div
    v-if="isOpen"
    class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
  >
    <div>
      <div class="flex justify-end">
        <button
          @click="$emit('close')"
          class="px-1 py-1 bg-primaryLight rounded-full mb-8 hover:bg-gray-300"
        >
          <img
            src="@/assets/tasks/iconoir_cancel.svg"
            alt="Close"
            class="h-6"
          />
        </button>
      </div>

      <div
        class="flex gap-4 bg-primaryLight rounded-[12px] w-[854px] max-w-full"
      >
        <div class="bg-white p-6 w-[554px]">
          <!-- Custom Calendar -->
          <div class="mb-4">
            <!-- Calendar Header -->
            <div class="flex items-center justify-between mb-4">
              <h2 class="text-[14px] font-semibold">Date range picker</h2>

              <div class="flex items-center justify-between">
                <div class="flex items-center justify-between">
                  <button
                    @click="previousMonth"
                    class="p-2 hover:bg-gray-100 rounded"
                  >
                    <svg
                      class="w-3 h-3"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M15 19l-7-7 7-7"
                      ></path>
                    </svg>
                  </button>
                  <h3 class="text-[14px] font-medium">{{ currentYear }}</h3>
                  <button
                    @click="nextMonth"
                    class="p-2 hover:bg-gray-100 rounded"
                  >
                    <svg
                      class="w-3 h-3"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M9 5l7 7-7 7"
                      ></path>
                    </svg>
                  </button>
                </div>

                <div class="flex items-center justify-between">
                  <button
                    @click="previousMonth"
                    class="p-2 hover:bg-gray-100 rounded"
                  >
                    <svg
                      class="w-3 h-3"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M15 19l-7-7 7-7"
                      ></path>
                    </svg>
                  </button>
                  <h3 class="text-[14px] font-medium">
                    {{ monthNames[currentMonth] }}
                  </h3>
                  <button
                    @click="nextMonth"
                    class="p-2 hover:bg-gray-100 rounded"
                  >
                    <svg
                      class="w-3 h-3"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M9 5l7 7-7 7"
                      ></path>
                    </svg>
                  </button>
                </div>
              </div>
            </div>

            <!-- Days of Week -->
            <div class="grid grid-cols-7 gap-1 mb-2">
              <div
                v-for="day in dayNames"
                :key="day"
                class="text- text-[13px] font-semibold text-gray-500 p-2"
              >
                {{ day }}
              </div>
            </div>

            <!-- Calendar Days -->
            <div class="grid grid-cols-7 gap-6">
              <div
                v-for="date in calendarDays"
                :key="date.key"
                @click="selectDate(date)"
                :class="[
                  'text-center w-8 h-8 p-1.5 cursor-pointer rounded-[100px] text-[13px]',
                  date.isCurrentMonth ? 'text-gray-900' : 'text-gray-300',
                  date.isToday ? 'bg-blue-100 font-semibold' : '',
                  date.isSelected ? 'bg-[#DFF3DF] text-primaryDark' : '',
                  date.isInRange ? 'bg-[#DFF3DF]' : '',
                  date.isCurrentMonth && !date.isSelected
                    ? 'hover:bg-gray-100'
                    : '',
                ]"
              >
                {{ date.day }}
              </div>
            </div>
          </div>

          <div class="mt-8 flex justify-between space-x-2">
            <!-- Selected Date Range Display -->
            <div class="rounded">
              <div class="flex gap-6 text-[11px]">
                <div class="space-x-2">
                  <span class="font-medium">From</span>
                  <span
                    @click="startEditingDate('start')"
                    :class="[
                      'text-[#14C100] rounded-[100px] px-1.5 py-1 cursor-pointer transition-colors',
                      editingDate === 'start' 
                        ? 'bg-[#B8E6B8] ring-2 ring-[#14C100]' 
                        : 'bg-[#DFF3DF] hover:bg-[#C5E8C5]'
                    ]"
                    >{{
                      startDate ? formatDate(startDate) : "Select start date"
                    }}</span
                  >
                </div>
                <div class="space-x-2">
                  <span class="font-medium">To</span>
                  <span
                    @click="startEditingDate('end')"
                    :class="[
                      'text-[#2F6DF7] rounded-[100px] px-1.5 py-1 cursor-pointer transition-colors',
                      editingDate === 'end' 
                        ? 'bg-[#A8C4F8] ring-2 ring-[#2F6DF7]' 
                        : 'bg-[#C5D6FD] hover:bg-[#A8C4F8]'
                    ]"
                    >{{
                      endDate ? formatDate(endDate) : "Select end date"
                    }}</span
                  >
                </div>
              </div>
            </div>

            <div class="flex space-x-2">
              <button
                @click="handleClear"
                class="px-3 py-1 text-[10px] font-medium text-gray-700 border border-grayDark rounded-[8px] hover:bg-gray-200"
              >
                Reset
              </button>
              <button
                @click="handleApply"
                :disabled="!startDate || !endDate"
                class="px-3 py-1 text-[10px] font-medium text-white bg-primaryDark rounded-[8px] hover:bg-grayDark disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                Set Filter Range
              </button>
            </div>
          </div>
        </div>

        <div class="w-[] border border-r-grayLight px-4 pt-4 space-y-4">
          <div
            @click="selectQuickRange(week)"
            v-for="week in standaloneWeeks"
            :key="week"
            :class="[
              'cursor-pointer flex justify-center items-center w-[284px] h-[41px] px-12 rounded-[8px] border border-grayLight hover:bg-gray-100',
              selectedQuickRange === week
                ? 'bg-[#DFF3DF] text-primaryDark font-semibold'
                : '',
            ]"
          >
            <p>{{ week }}</p>
          </div>

          <div class="grid grid-cols-2 gap-4">
            <div
              v-for="week in groupedWeeks"
              :key="week + '-grid'"
              @click="selectQuickRange(week)"
              :class="[
                'cursor-pointer flex justify-center items-center w-full h-[41px] py-4 rounded-[8px] border border-grayLight hover:bg-gray-100',
                selectedQuickRange === week
                  ? 'bg-[#DFF3DF] text-primaryDark font-semibold'
                  : '',
              ]"
              class="flex justify-center items-center w-full h-[41px] py-4 rounded-[8px] border border-grayLight"
            >
              <p>{{ week }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "DateFilterModal",
  props: {
    isOpen: {
      type: Boolean,
      default: false,
    },
    initialStartDate: {
      type: String,
      default: "",
    },
    initialEndDate: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      currentMonth: new Date().getMonth(),
      currentYear: new Date().getFullYear(),
      startDate: null,
      endDate: null,
      editingDate: null, // Track which date is being edited: 'start' or 'end'

      dayNames: ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"],
      weekNames: [
        "Today",
        "Yesterday",
        "Last 7 days",
        "This Week",
        "Last Week",
        "This Month",
        "Last Month",
        "This Year",
        "Last Year",
      ],
      selectedQuickRange: null,
    };
  },
  computed: {
    calendarDays() {
      const days = [];
      const firstDay = new Date(this.currentYear, this.currentMonth, 1);
      // const lastDay = new Date(this.currentYear, this.currentMonth + 1, 0);
      const startDate = new Date(firstDay);
      startDate.setDate(startDate.getDate() - firstDay.getDay());

      const today = new Date();
      today.setHours(0, 0, 0, 0);

      for (let i = 0; i < 42; i++) {
        const date = new Date(startDate);
        date.setDate(startDate.getDate() + i);

        const dateString = this.dateToString(date);
        const isCurrentMonth = date.getMonth() === this.currentMonth;
        const isToday = date.getTime() === today.getTime();
        const isSelected = this.isDateSelected(date);
        const isInRange = this.isDateInRange(date);

        days.push({
          key: dateString,
          day: date.getDate(),
          date: new Date(date),
          dateString,
          isCurrentMonth,
          isToday,
          isSelected,
          isInRange,
        });
      }

      return days;
    },
    monthNames() {
      return [
        "January",
        "February",
        "March",
        "April",
        "May",
        "June",
        "July",
        "August",
        "September",
        "October",
        "November",
        "December",
      ];
    },
    standaloneWeeks() {
      return this.weekNames.filter(
        (week) =>
          week === "Today" || week === "Yesterday" || week === "Last 7 days"
      );
    },
    groupedWeeks() {
      return this.weekNames.filter(
        (week) =>
          week !== "Today" && week !== "Yesterday" && week !== "Last 7 days"
      );
    },
  },
  watch: {
    initialStartDate: {
      immediate: true,
      handler(newVal) {
        this.startDate = newVal ? new Date(newVal) : null;
      },
    },
    initialEndDate: {
      immediate: true,
      handler(newVal) {
        this.endDate = newVal ? new Date(newVal) : null;
      },
    },
    isOpen(newVal) {
      if (newVal) {
        // Reset to current month when modal opens
        const now = new Date();
        this.currentMonth = now.getMonth();
        this.currentYear = now.getFullYear();
      }
    },
  },
  methods: {
    previousMonth() {
      if (this.currentMonth === 0) {
        this.currentMonth = 11;
        this.currentYear--;
      } else {
        this.currentMonth--;
      }
    },
    nextMonth() {
      if (this.currentMonth === 11) {
        this.currentMonth = 0;
        this.currentYear++;
      } else {
        this.currentMonth++;
      }
    },
    selectDate(dateObj) {
      if (!dateObj.isCurrentMonth) return;
        this.selectedQuickRange = null;


      const selectedDate = new Date(dateObj.date);

      if (this.editingDate === 'start') {
        // Editing start date
        this.startDate = selectedDate;
        this.editingDate = null;
        // If new start date is after end date, clear end date
        if (this.endDate && selectedDate > this.endDate) {
          this.endDate = null;
        }
      } else if (this.editingDate === 'end') {
        // Editing end date
        this.endDate = selectedDate;
        this.editingDate = null;
        // If new end date is before start date, clear start date
        if (this.startDate && selectedDate < this.startDate) {
          this.startDate = null;
        }
      } else if (!this.startDate || (this.startDate && this.endDate)) {
        // First selection or reset selection
        this.startDate = selectedDate;
        this.endDate = null;
      } else if (this.startDate && !this.endDate) {
        // Second selection
        if (selectedDate < this.startDate) {
          // If second date is before first, swap them
          this.endDate = this.startDate;
          this.startDate = selectedDate;
        } else {
          this.endDate = selectedDate;
        }
      }
    },
    startEditingDate(dateType) {
      this.editingDate = dateType;
    },
    cancelEditing() {
      this.editingDate = null;
    },
    isDateSelected(date) {
      const dateString = this.dateToString(date);
      const startString = this.startDate
        ? this.dateToString(this.startDate)
        : null;
      const endString = this.endDate ? this.dateToString(this.endDate) : null;

      return dateString === startString || dateString === endString;
    },
    isDateInRange(date) {
      if (!this.startDate || !this.endDate) return false;

      return date > this.startDate && date < this.endDate;
    },
    dateToString(date) {
      const d = new Date(date);
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    },
    formatDate(date) {
      if (!date) return "";

      const options = {
        year: "numeric",
        month: "short",
        day: "numeric",
      };
      return date.toLocaleDateString("en-US", options);
    },
    handleApply() {
      this.$emit("apply", {
        startDate: this.startDate ? this.dateToString(this.startDate) : "",
        endDate: this.endDate ? this.dateToString(this.endDate) : "",
      });
      this.$emit("close");
    },
    handleClear() {
      this.startDate = null;
      this.endDate = null;
      this.$emit("apply", {
        startDate: "",
        endDate: "",
      });
    },
    selectQuickRange(label) {
      this.selectedQuickRange = label;
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const startOfWeek = (date) => {
        const d = new Date(date);
        d.setDate(d.getDate() - d.getDay());
        return new Date(d.setHours(0, 0, 0, 0));
      };

      const endOfWeek = (date) => {
        const d = new Date(date);
        d.setDate(d.getDate() + (6 - d.getDay()));
        return new Date(d.setHours(0, 0, 0, 0));
      };

      const startOfMonth = (date) => new Date(date.getFullYear(), date.getMonth(), 1);
      const endOfMonth = (date) => new Date(date.getFullYear(), date.getMonth() + 1, 0);

      const startOfYear = (date) => new Date(date.getFullYear(), 0, 1);
      const endOfYear = (date) => new Date(date.getFullYear(), 11, 31);

      if (label === "Today") {
        this.startDate = new Date(today);
        this.endDate = new Date(today);
      } else if (label === "Yesterday") {
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        this.startDate = new Date(yesterday);
        this.endDate = new Date(yesterday);
      } else if (label === "Last 7 days") {
        const start = new Date(today);
        start.setDate(start.getDate() - 6);
        this.startDate = start;
        this.endDate = today;
      } else if (label === "This Week") {
        this.startDate = startOfWeek(today);
        this.endDate = endOfWeek(today);
      } else if (label === "Last Week") {
        const lastWeekStart = startOfWeek(today);
        lastWeekStart.setDate(lastWeekStart.getDate() - 7);
        const lastWeekEnd = new Date(lastWeekStart);
        lastWeekEnd.setDate(lastWeekEnd.getDate() + 6);
        this.startDate = lastWeekStart;
        this.endDate = lastWeekEnd;
      } else if (label === "This Month") {
        this.startDate = startOfMonth(today);
        this.endDate = endOfMonth(today);
      } else if (label === "Last Month") {
        const lastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        this.startDate = startOfMonth(lastMonth);
        this.endDate = endOfMonth(lastMonth);
      } else if (label === "This Year") {
        this.startDate = startOfYear(today);
        this.endDate = endOfYear(today);
      } else if (label === "Last Year") {
        const lastYear = new Date(today.getFullYear() - 1, 0, 1);
        this.startDate = startOfYear(lastYear);
        this.endDate = endOfYear(lastYear);
      }

      // 👇 Update calendar view to selected startDate's month/year
      this.currentMonth = this.startDate.getMonth();
      this.currentYear = this.startDate.getFullYear();
    }


  },
};
</script>

<style scoped>
/* Additional custom styles can be added here if needed */
</style>
